import querystringify from "querystringify";
import * as React from "react";
import LocalStorageService from "../../auth/local-storage-service";
import { UserService } from "../../services/user-service";
import { IWithHistoryProp, withHistory } from "../../shared/hoc/with-history";
import { KMainFunctions } from "../../shared/utility/main-functions";
import AuthVerifyEmailSvg from "../../svgs/auth-verify-email.svg";
import AuthWorkspaceJoinedSvg from "../../svgs/auth-workspace-joined.svg";
import KuikaButton from "../ui-kit/kuika-button/kuika-button";
import KuikaSpinner from "../ui-kit/kuika-spinner/kuika-spinner";
import KuikaH1 from "../ui-kit/typography/H1/H1";
import KuikaH4 from "../ui-kit/typography/H4/H4";
import KuikaText from "../ui-kit/typography/Text/Text";
import { AuthLogo } from "./auth-logo";

export interface IConfirmEmailState {
  isRequesting: boolean;
  workspace?: string;
  countdown: number;
}

export interface IConfirmEmailProps {}

export interface IPageQueries {
  token: string;
  username: string;
  domainName?: string;
}

class ConfirmEmail extends React.PureComponent<IConfirmEmailProps & IWithHistoryProp, IConfirmEmailState> {
  timer: NodeJS.Timeout | null = null;

  constructor(props: IConfirmEmailProps & IWithHistoryProp) {
    super(props);
    this.state = {
      isRequesting: true,
      countdown: 5
    };
    LocalStorageService.clearToken();
  }

  componentDidMount = () => {
    this.confirmEmailRequest();
  };

  componentWillUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  async confirmEmailRequest() {
    try {
      let workspace: string | undefined;
      const querySearch = this.props?.navigator?.location?.search;
      //
      if (!querySearch) throw new Error('No invitation info found, redirecting to "sign up" page.');

      const queryParams = querystringify.parse(querySearch) as IPageQueries;
      const res = await UserService.ConfirmEmail({ token: queryParams.token, username: queryParams.username });

      if (res.status !== 200) throw Object.assign(new Error(), res);

      workspace = queryParams.domainName;

      this.setState({ isRequesting: false, workspace }, () => {
        this.timer = setInterval(() => {
          this.setState(
            (prevState) => ({
              countdown: prevState.countdown - 1
            }),
            () => {
              if (this.state.countdown <= 0) {
                if (this.timer) clearInterval(this.timer);
                if (workspace) {
                  this.props.navigator.goToExactDomain(workspace, true);
                  return;
                }
                this.props.navigator.gotoRoot();
              }
            }
          );
        }, 1000);
      });
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      setTimeout(() => {
        this.props.navigator.gotoRoot();
      }, 2000);
      return;
    }
  }

  renderLoading() {
    return (
      <div>
        <KuikaSpinner spinning={true} size="large" />
      </div>
    );
  }

  renderActivation() {
    const isMainAI = KMainFunctions.isMainAI();
    return (
      <div className="email-confirmation">
        <div className="email-confirmation__content">
          <div className="email-confirmation__top-section">
            <AuthLogo />

            <div className="email-confirmation__check-inbox-card">
              <div className="email-confirmation__title-row">
                {this.state.workspace ? <AuthWorkspaceJoinedSvg /> : <AuthVerifyEmailSvg />}
                <KuikaH4>{this.state.workspace ? "Workspace joined" : "Success activation"}</KuikaH4>
              </div>

              <div className="email-confirmation__line" />

              <div className="email-confirmation__countdown-wrapper">
                <KuikaH1 style={{ color: "var(--dark-8)", margin: 0 }}>{this.state.countdown}</KuikaH1>
              </div>

              <KuikaText className="email-confirmation__message-text">
                {this.state.workspace ? (
                  <>
                    You have accepted the <span style={{ fontWeight: 700 }}>{this.state.workspace}</span> workspace
                    invitation. You can switch to other workspaces from the Apps screen. You are being redirected to
                    your account.
                  </>
                ) : (
                  "Your account activation is complete. You are being redirected to your account."
                )}
              </KuikaText>
            </div>
          </div>

          {!isMainAI && (
            <div className="email-confirmation__bottom-section">
              <KuikaButton
                type="text"
                size="sm"
                uppercase
                onClick={() => window.open("https://www.kuika.com/en/legal/privacy", "_blank")}
              >
                Privacy
              </KuikaButton>
              <KuikaButton
                type="text"
                size="sm"
                uppercase
                onClick={() => window.open("https://www.kuika.com/en/legal/terms", "_blank")}
              >
                Terms
              </KuikaButton>
            </div>
          )}
        </div>
      </div>
    );
  }

  render() {
    return this.state.isRequesting ? this.renderLoading() : this.renderActivation();
  }
}

const confirmEmail = withHistory<IConfirmEmailProps>(ConfirmEmail);
export { confirmEmail as ConfirmEmail };
